import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import { usePreferences } from '../../contexts/PreferencesContext';
import { ROLE_LABELS, rolesOfUser, type Role } from '../../types/practiceArea';
import { enterRole, ROLE_LENS, type InAppRole } from '../../utils/activeRole';

/**
 * Shown when the matter API answers 403 `wrong_workspace`: the lawyer owns
 * the matter, it just lives in the other workspace than this tab's role
 * (QA Q11 — this used to read "Matter not found"). Offers the switch only
 * into an in-app workspace the lawyer actually holds — the same set the login
 * chooser offers — and reloads, the PracticeAreaSwitcher pattern, so every
 * surface (sidebar, interceptor, dashboard) re-scopes to the new role at once.
 */
/** `compact`: one-line strip for the matter context banner above tool pages. */
const WrongWorkspaceNotice: React.FC<{ role: Role; compact?: boolean }> = ({ role, compact = false }) => {
  const navigate = useNavigate();
  const profile = useSelector((s: RootState) => s.auth.user);
  const { setActivePracticeArea } = usePreferences();
  const [switching, setSwitching] = useState(false);

  const label = ROLE_LABELS[role] ?? 'other';
  const canSwitch =
    (role === 'criminal' || role === 'civil') && rolesOfUser(profile?.practice_areas).has(role);

  const switchAndOpen = async () => {
    setSwitching(true);
    const r = role as InAppRole;
    enterRole(r);
    try {
      await setActivePracticeArea(ROLE_LENS[r]);
    } catch {
      /* the lens also persists to localStorage optimistically */
    }
    window.location.reload();
  };

  const switchButton = canSwitch && (
    <button
      type="button"
      disabled={switching}
      onClick={() => void switchAndOpen()}
      className="px-3 py-1.5 text-sm font-medium bg-brass text-chamber-ink hover:bg-brass-hover disabled:opacity-60 transition-colors"
    >
      {switching ? 'Switching…' : `Switch to ${label} and open`}
    </button>
  );

  if (compact) {
    return (
      <div
        role="alert"
        className="mb-6 flex flex-wrap items-center justify-between gap-2 border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-chamber-panel"
      >
        <span>
          This matter is in your {label} workspace
          {canSwitch ? ' — this tab is open in a different one.' : ', which is not on your profile.'}
        </span>
        {switchButton}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] text-center" role="alert">
      <p className="text-sm text-chamber-panel mb-1">This matter is in your {label} workspace.</p>
      <p className="text-xs text-parchment-muted-warm mb-4">
        {canSwitch
          ? 'This tab is open in a different workspace.'
          : 'That workspace is not on your profile — add it in Settings to open this matter.'}
      </p>
      <div className="flex gap-2">
        {switchButton}
        <button
          type="button"
          onClick={() => navigate('/')}
          className="px-3 py-1.5 text-sm font-medium text-brass-ink hover:bg-parchment-hover transition-colors"
        >
          Back to dashboard
        </button>
      </div>
    </div>
  );
};

export default WrongWorkspaceNotice;
